import { Entity, FieldType, FileStorageType, PersistenceType } from 'gossamer-unified';
import { PERSONAL_INSTITUTION_ID } from 'cb-unified';
import { Institution } from './Institution';
import {
  AttendanceCaptureTimeCutoffOptions,
  EventFlowOptions,
  StatusOptions,
  VisibilityLimitOptions,
  YesNoOptions,
} from './SharedOptions';
import { FeedbackForm } from './FeedbackForm';
import { BookingPaymentTerms } from './BookingPaymentTerms';

export interface EventProfileRow {
  id: number;
  title: string;
  status: string;
  seq: number;
  institution_id: number;
  image: string;
  visibility_limit: string;
  flow_option: string;
  enforce_attendance_geofence: string;
  attendance_capture_time_cutoff: string;
  permit_self_sign_esig: string;
  feedback_form_id: number;
  cohost_summary_email: string;
  booking_payment_terms_id: number | null;
  certificate_file: string;
  host_certificate_file: string;
}

export const EventProfile: Entity<EventProfileRow> = {
  id: 'EventProfile',
  title: 'Event Profile',
  service: 'main',
  rowLabel: (row: EventProfileRow) => row.title,
  primaryKeyFields: ['id'],
  source: {
    tableName: 'public.event_profile',
  },
  security: [
    { role: 'user', action: 'read', access: 'conditional', condition: `institution_id = ${PERSONAL_INSTITUTION_ID}` },
    {
      role: 'admin',
      action: 'read',
      access: 'conditional',
      condition: `institution_id IN (${PERSONAL_INSTITUTION_ID}, {adminInstitutionId})`,
    },
    { role: 'admin', action: 'all', access: 'conditional', condition: 'institution_id = {adminInstitutionId}' },
    { role: 'staff', action: 'all', access: 'all' },
  ],
  defaultSort: [['seq', 'ASC']],
  defaultFilters: [
    { fieldId: 'title', operator: 'sw', value: '' },
    { fieldId: 'status', operator: 'eq', value: 'ACTIVE', display: 'hidden' },
  ],
  listRowLink: '/admin/event-profile/display/{key}',
  fields: {
    id: {
      seq: 100,
      type: FieldType.Integer,
      label: 'Id',
      fieldGroup: 'basic',
      persistence: PersistenceType.BackEndManaged,
      mandatory: true,
      listColumn: true,
      autoIncrement: true,
    },
    title: {
      seq: 110,
      type: FieldType.Text,
      label: 'Title',
      fieldGroup: 'basic',
      mandatory: true,
      listColumn: true,
      maxLength: 200,
    },
    status: {
      seq: 120,
      type: FieldType.Options,
      label: 'Status',
      fieldGroup: 'basic',
      listColumn: true,
      mandatory: true,
      options: StatusOptions,
    },
    seq: {
      seq: 130,
      type: FieldType.Integer,
      label: 'Sequence',
      fieldGroup: 'basic',
      listColumn: true,
      description: 'Used for ordering profiles in lists',
    },
    institution_id: {
      seq: 140,
      type: FieldType.Reference,
      persistence: PersistenceType.SetOnCreateOnly,
      label: 'Institution',
      fieldGroup: 'basic',
      refEntity: Institution,
      mandatory: true,
      listColumn: true,
    },
    image: {
      seq: 160,
      type: FieldType.File,
      label: 'Default Main Image',
      fieldGroup: 'engagement',
      imagesOnly: true,
      storageType: FileStorageType.ExternalResource,
    },
    visibility_limit: {
      seq: 200,
      type: FieldType.Options,
      label: 'Visibility Limit',
      fieldGroup: 'audience',
      mandatory: true,
      options: VisibilityLimitOptions,
    },
    booking_payment_terms_id: {
      seq: 210,
      type: FieldType.Reference,
      label: 'Booking Payment Terms',
      refEntity: BookingPaymentTerms,
      fieldGroup: 'audience',
      persistence: PersistenceType.BackEndManaged, // not updateable through FE
    },
    flow_option: {
      seq: 300,
      label: 'Post-Event Flow',
      fieldGroup: 'post-event-flow',
      type: FieldType.Options,
      mandatory: true,
      description: 'Whether to track attendance, capture feedback and issue certificate',
      options: EventFlowOptions,
    },
    enforce_attendance_geofence: {
      seq: 310,
      type: FieldType.Options,
      label: 'Enforce Attendance Geofence',
      fieldGroup: 'post-event-flow',
      description:
        'Whether to allow attendance to be recorded if outside the GPS Location, for events requiring attendance tracking and where a GPS Location is specified',
      mandatory: true,
      options: [
        { id: 'none', label: 'none' },
        { id: 'qr-code-only', label: 'QR code only' },
        { id: 'e-sig-only', label: 'e-signature only' },
        { id: 'both', label: 'both QR code and e-signature' },
      ],
    },
    attendance_capture_time_cutoff: {
      seq: 320,
      type: FieldType.Options,
      label: 'Attendance Capture Time Cutoff',
      fieldGroup: 'post-event-flow',
      description: 'Limit the time during which attendees can record their attendance for the event',
      mandatory: true,
      options: AttendanceCaptureTimeCutoffOptions,
    },
    permit_self_sign_esig: {
      seq: 330,
      type: FieldType.Options,
      label: 'Permit Self Sign e-signature',
      fieldGroup: 'post-event-flow',
      description: 'Whether in-app text permits attendees to self-sign electronically for attendance',
      mandatory: true,
      options: [
        { id: 'yes', label: 'yes' },
        { id: 'no', label: 'no' },
      ],
    },
    feedback_form_id: {
      seq: 340,
      type: FieldType.Reference,
      label: 'Feedback Form',
      fieldGroup: 'post-event-flow',
      refEntity: FeedbackForm,
    },
    cohost_summary_email: {
      seq: 350,
      type: FieldType.Options,
      label: 'Email Co-Hosts a Post-Event Summary',
      fieldGroup: 'post-event-flow',
      description: 'Whether co-hosts are emailed a summary of attendance and feedback 24 hours after the event ends',
      mandatory: true,
      options: YesNoOptions,
    },
    certificate_file: {
      seq: 400,
      type: FieldType.File,
      label: 'Attendee Certificate Template',
      fieldGroup: 'certificates',
      description:
        'Background image file for certificates issued for events with this profile. Must be a .jpg or .jpeg image file with blank area in the middle for dynamic text. Should be A4 landscape ideally - otherwise will be stretched to fit. Max file size 5MB.',
      imagesOnly: true,
      allowedExtensions: ['.jpg', '.jpeg'],
      storageType: FileStorageType.ExternalResource,
    },
    host_certificate_file: {
      seq: 410,
      type: FieldType.File,
      label: 'Host Certificate Template',
      fieldGroup: 'certificates',
      description:
        'Background image file for host certificates issued for events with this profile. Must be a .jpg or .jpeg image file with blank area in the middle for dynamic text. Should be A4 landscape ideally - otherwise will be stretched to fit. Max file size 5MB. Host certificates will only be produced if this is supplied.',
      imagesOnly: true,
      allowedExtensions: ['.jpg', '.jpeg'],
      storageType: FileStorageType.ExternalResource,
    },
  },
};
